import { useState } from "react";

import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import FlagIcon from "@uui/Flag01";
import HelpSquareIcon from "@uui/HelpSquare";
import LogoutIcon from "@uui/LogOut01";
import DesktopIcon from "@uui/Monitor01";
import RocketIcon from "@uui/Rocket02";
import SettingsIcon from "@uui/Settings04";
import WrenchIcon from "@uui/Tool01";
import TranslateIcon from "@uui/Translate01";
import PeopleIcon from "@uui/UserPlus01";

import { Divider, MenuProps, Typography } from "@mui/material";

import useReadAdmin from "@app/analytics/hooks/useReadAdmin";
import useApps from "@app/common/hooks/useApps";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import useResponsiveView from "@app/common/hooks/useResponsiveView";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import type { User } from "@app/common/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import * as Workspace from "@app/common/workspaces/workspaceUtils";
import Config from "@app/config";

import Stack from "@rui/Stack";

import LocaleDialog from "./LocaleSwitcher/LocaleMenuDialog";
import SideNavMenu from "./SideNav/SideNavMenu";
import SideNavMenuItem from "./SideNav/SideNavMenuItem";

interface Props extends MenuProps {
  onClose: () => void;
  onRequestDesktop?: () => void;
  user?: User | null;
}

const UserNavMenu = ({ onClose, onRequestDesktop, user, ...props }: Props) => {
  const { isMobile, inMobileView } = useResponsiveView();
  const { inWorkspace, workspaceUser } = useWorkspace();
  const { canCreateWorkspace } = useFeatureAccess();
  const { isAdmin } = useReadAdmin();
  const { client } = useApps();
  const { i18n } = useLingui();

  const [localeDialogOpen, setLocaleDialogOpen] = useState(false);

  const showLogout = client !== "zoom";
  const showRequestDesktop = onRequestDesktop != null && isMobile;

  const showLocaleSwitcher = Config.flags.enableSpanishLocale && inMobileView;

  return (
    <SideNavMenu disableAutoFocusItem onClose={onClose} {...props}>
      {user && (
        <Stack padding={1} justifyContent="space-between" width="190px">
          <Typography variant="bodySecondaryMed" noWrap>
            {user.fullName}
          </Typography>
          <Typography variant="labelReg" color="shades.neutral.500" noWrap>
            {user.email}
          </Typography>
        </Stack>
      )}
      {/* menu component doesn't accept fragments so here we are... */}
      {user && <Divider />}
      {isAdmin && (
        <SideNavMenuItem
          data-test-id="side-nav-admin"
          to={ROUTES.READ_ADMIN}
          Icon={FlagIcon}
          onClick={onClose}
        >
          Admin
        </SideNavMenuItem>
      )}
      {/* menu component doesn't accept fragments so here we are... */}
      {isAdmin && <Divider />}
      <SideNavMenuItem
        data-test-id="side-nav-settings"
        to={ROUTES.USER_SETTINGS_DEFAULT_SECTION}
        Icon={SettingsIcon}
        onClick={onClose}
      >
        <Trans>Account Settings</Trans>
      </SideNavMenuItem>
      <SideNavMenuItem
        data-test-id="side-nav-billing"
        to={ROUTES.BILLING}
        Icon={RocketIcon}
        onClick={onClose}
      >
        Plan & Billing
      </SideNavMenuItem>
      {inWorkspace ? (
        <SideNavMenuItem
          data-test-id="side-nav-workspace"
          to={ROUTES.WORKSPACE_SETTINGS_ROOT}
          Icon={WrenchIcon}
          onClick={onClose}
        >
          {Workspace.isWorkspaceAdmin(workspaceUser)
            ? "Manage Workspace"
            : "Workspace Settings"}
        </SideNavMenuItem>
      ) : canCreateWorkspace ? (
        <SideNavMenuItem
          Icon={PeopleIcon}
          to={ROUTES.NEW_WORKSPACE}
          onClick={onClose}
        >
          Add People
        </SideNavMenuItem>
      ) : null}
      <Divider />
      <SideNavMenuItem
        data-test-id="side-nav-support"
        Icon={HelpSquareIcon}
        href={LINKS.SUPPORT.ROOT}
        onClick={onClose}
        newTab
      >
        Support
      </SideNavMenuItem>
      {showLocaleSwitcher && (
        <SideNavMenuItem
          data-test-id="side-nav-language"
          Icon={TranslateIcon}
          onClick={() => {
            setLocaleDialogOpen(true);
          }}
        >
          <Trans>Language</Trans>
          {` (${i18n.locale.toUpperCase()})`}
        </SideNavMenuItem>
      )}
      {showRequestDesktop && (
        <SideNavMenuItem
          data-test-id="side-nav-support"
          Icon={DesktopIcon}
          onClick={onRequestDesktop}
        >
          View Desktop Site
        </SideNavMenuItem>
      )}
      {showLogout && <Divider />}
      {showLogout && (
        <SideNavMenuItem
          data-test-id="side-nav-logout"
          to={ROUTES.SIGN_OUT}
          Icon={LogoutIcon}
          onClick={onClose}
        >
          Log Out
        </SideNavMenuItem>
      )}
      {showLocaleSwitcher && (
        <LocaleDialog
          open={localeDialogOpen}
          onClose={() => {
            setLocaleDialogOpen(false);
            onClose();
          }}
        />
      )}
    </SideNavMenu>
  );
};

export default UserNavMenu;
